import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import {
  Pipeline,
  Fund,
  PipelineShareLink,
  STAGE_COLORS,
} from '../types/fundraise';
import { formatAmount, formatCurrency } from '../utils/helpers';
import { useAuth } from '../contexts/AuthContext';
import { stashPendingClaim } from '../utils/pendingCollabClaim';

interface PublicPipelinePayload {
  pipeline: Pipeline;
  funds: Fund[];
  shareLink: PipelineShareLink;
}

const API_BASE_URL =
  process.env.REACT_APP_API_URL || 'https://api.sendupdates.ai';

const fetchPublicPipeline = async (
  linkSlug: string | undefined,
  token: string | undefined
): Promise<PublicPipelinePayload> => {
  const url = linkSlug
    ? `${API_BASE_URL}/api/public/pipeline/slug/${encodeURIComponent(linkSlug)}`
    : `${API_BASE_URL}/api/public/pipeline/${encodeURIComponent(token || '')}`;
  const response = await axios.get(url);
  return response.data.data;
};

const fundLogoInitials = (fund: Fund): string => {
  if (fund.logo_initials) return fund.logo_initials.toUpperCase().slice(0, 2);
  const trimmed = fund.name.trim();
  if (!trimmed) return '??';
  const words = trimmed.split(/\s+/);
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
};

const amountRange = (min: number | null, max: number | null): string => {
  if (min === null && max === null) return '';
  if (min === null) return formatAmount(max);
  if (max === null) return formatAmount(min);
  return `${formatAmount(min)} – ${formatAmount(max)}`;
};

// Brand chrome — same shape as PublicDataRoomView so all SendUpdates-shared
// pages feel like one product. Top bar + footer share the same wordmark and
// design tokens; the body uses the existing fundraise kanban classes.
const BrandShell: React.FC<{
  children: React.ReactNode;
  label: string;
}> = ({ children, label }) => (
  <div style={pageWrapperStyle}>
    <div style={topBarStyle}>
      <div style={topBarInnerStyle}>
        <a
          href="https://sendupdates.ai"
          target="_blank"
          rel="noopener noreferrer"
          style={{ textDecoration: 'none' }}
        >
          <span
            style={{
              color: 'var(--su-purple-accent)',
              fontWeight: 700,
              fontSize: 18,
              letterSpacing: 0.2,
            }}
          >
            sendupdates.ai
          </span>
        </a>
        <span style={{ fontSize: 12, color: 'var(--su-text-subtle)' }}>{label}</span>
      </div>
    </div>
    <div style={pageBodyStyle}>{children}</div>
    <div style={footerStyle}>
      Shared via{' '}
      <a
        href="https://sendupdates.ai"
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: 'var(--su-purple-accent)' }}
      >
        SendUpdates.ai
      </a>
    </div>
  </div>
);

const PublicPipelineView: React.FC = () => {
  const { linkSlug, token } = useParams<{ linkSlug?: string; token?: string }>();
  const { isAuthenticated } = useAuth();
  const [data, setData] = useState<PublicPipelinePayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const payload = await fetchPublicPipeline(linkSlug, token);
        if (!cancelled) setData(payload);
      } catch (err: any) {
        if (cancelled) return;
        const status = err?.response?.status;
        if (status === 404) {
          setError('This share link has been deactivated or has expired.');
        } else {
          setError(err?.message || 'Failed to load shared pipeline');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [linkSlug, token]);

  if (loading) {
    return (
      <BrandShell label="Shared Pipeline">
        <div style={emptyStateStyle}>Loading shared pipeline…</div>
      </BrandShell>
    );
  }

  if (error || !data) {
    return (
      <BrandShell label="Shared Pipeline">
        <div style={emptyStateStyle}>
          <h2 style={{ color: 'var(--su-text-primary)', fontSize: 22, fontWeight: 700, marginBottom: 8 }}>
            Link unavailable
          </h2>
          <p style={{ color: 'var(--su-text-muted)' }}>
            {error || 'This share link is no longer valid.'}
          </p>
        </div>
      </BrandShell>
    );
  }

  const { pipeline, funds, shareLink } = data;
  const stages = pipeline.stages || [];
  const isCollaborateLink = shareLink.permission_level === 'collaborate';
  // Authed visitor → claim page directly; unauthed → signup with the token
  // as a query param so the post-signup modal can resurface the invite.
  const claimPath = isAuthenticated
    ? `/fundraise/claim/${encodeURIComponent(shareLink.share_token)}`
    : `/signup?claim=${encodeURIComponent(shareLink.share_token)}`;
  const claimLabel = isAuthenticated
    ? 'Accept invite & collaborate'
    : 'Sign up to collaborate';

  const handleAcceptClick = (e: React.MouseEvent) => {
    // For unauthed visitors, also stash the token in localStorage right now
    // so we can recover it after sign-in even if the signup page never
    // sees the query param (e.g. user closes tab, clicks the link again
    // after creating an account through another channel).
    if (!isAuthenticated) {
      e.preventDefault();
      stashPendingClaim(shareLink.share_token);
      window.location.href = claimPath;
    }
  };

  const fundsByStage: Record<string, Fund[]> = {};
  stages.forEach((s) => {
    fundsByStage[s.id] = [];
  });
  funds.forEach((fund) => {
    if (fundsByStage[fund.stage]) fundsByStage[fund.stage].push(fund);
  });
  Object.keys(fundsByStage).forEach((stageId) => {
    fundsByStage[stageId].sort((a, b) => a.position - b.position);
  });

  return (
    <BrandShell label={isCollaborateLink ? 'Collaborate Invite' : 'Shared Pipeline'}>
      <header className="su-public-pipeline-header">
        <div>
          <div className="su-public-pipeline-shared-label">Shared pipeline</div>
          <h1 className="su-public-pipeline-name">{pipeline.name}</h1>
          <div className="su-public-pipeline-target">
            Target: {formatCurrency(pipeline.target_amount)} · {funds.length}{' '}
            {funds.length === 1 ? 'fund' : 'funds'}
          </div>
        </div>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            gap: 12,
          }}
        >
          <span className="su-public-pipeline-readonly-badge">
            {isCollaborateLink ? 'Collaborate invite' : 'Read-only'}
          </span>
          {isCollaborateLink && (
            <a
              href={claimPath}
              onClick={handleAcceptClick}
              className="su-btn-primary"
              style={{ textDecoration: 'none' }}
            >
              {claimLabel}
            </a>
          )}
        </div>
      </header>

      <div className="su-kanban-board" aria-label="Pipeline funds by stage">
        {stages.map((stage) => {
          const stageFunds = fundsByStage[stage.id] || [];
          const dotColor = STAGE_COLORS[stage.color] || '#8b5cf6';
          return (
            <div className="su-kanban-column" key={stage.id}>
              <div className="su-kanban-column-header">
                <div className="su-kanban-column-title">
                  <span
                    className="su-kanban-column-dot"
                    style={{ backgroundColor: dotColor }}
                  />
                  <span>{stage.name}</span>
                </div>
                <span className="su-kanban-column-count">{stageFunds.length}</span>
              </div>
              <div className="su-kanban-column-content">
                {stageFunds.length === 0 ? (
                  <div className="su-public-pipeline-empty-stage">No funds</div>
                ) : (
                  stageFunds.map((fund) => (
                    <div className="su-fund-card" key={fund.id}>
                      <div
                        className="su-fund-card-logo"
                        style={{
                          backgroundColor: STAGE_COLORS[fund.logo_color] || '#8b5cf6',
                        }}
                      >
                        {fundLogoInitials(fund)}
                      </div>
                      <div className="su-fund-card-content">
                        <div className="su-fund-card-name" title={fund.name}>
                          {fund.name}
                        </div>
                        {(fund.min_amount !== null || fund.max_amount !== null) && (
                          <div className="su-fund-card-amount">
                            {amountRange(fund.min_amount, fund.max_amount)}
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>
    </BrandShell>
  );
};

// Brand chrome styles — mirror PublicDataRoomView so all shared-link pages
// feel like the same product to recipients.
const pageWrapperStyle: React.CSSProperties = {
  minHeight: '100vh',
  background: 'var(--su-color-black, #161616)',
  color: 'var(--su-text-primary, #ffffff)',
  display: 'flex',
  flexDirection: 'column',
};

const topBarStyle: React.CSSProperties = {
  borderBottom: '1px solid var(--su-border-primary, rgba(255,255,255,0.08))',
  background: 'var(--su-gray-900, #0a0a0a)',
};

const topBarInnerStyle: React.CSSProperties = {
  maxWidth: 1200,
  margin: '0 auto',
  padding: '14px 24px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
};

const pageBodyStyle: React.CSSProperties = {
  flex: 1,
  width: '100%',
  maxWidth: 1200,
  margin: '0 auto',
  padding: '32px 24px 48px',
  boxSizing: 'border-box',
};

const footerStyle: React.CSSProperties = {
  textAlign: 'center',
  padding: '24px 0 24px',
  fontSize: 12,
  color: 'var(--su-text-subtle, rgba(255,255,255,0.4))',
};

const emptyStateStyle: React.CSSProperties = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  padding: '80px 24px',
  color: 'var(--su-text-muted, rgba(255,255,255,0.7))',
};

export default PublicPipelineView;
